import React, { useEffect, useState } from 'react';
import { Box, Card, Container, TextField, Typography } from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import toast from 'react-hot-toast';
import { Fields } from '../../../ui-component/FormikFormLibrary/FormikTypes';
import { httpRequest } from 'src/services/httpRequest';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import PdfGenerator from 'src/ui-component/PdfGenerator';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { RootState } from 'src/redux-store/appStore';
import { useNavigate, useParams } from 'react-router-dom';
import { getFeesForCurrentPermit } from '../common/getFeesForCurrentPermit';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import { fileUpload } from '../common/uploadPDF';
import { getBlockLots } from '../common/getBlockLots';

const NewApplication: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { townshipId, permit } = useParams();
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const [loading, setLoading] = useState<boolean>(false);
  const [clickedOn, setClickedOn] = useState<string>('');
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 500
  });
  const [blockLotsOptions, setBlockLotsOptions] = useState([]);
  const [applicationDetails, setApplicationDetails] = useState({
    ApplicantName: '',
    ApplicantAddress: '',
    BlockLot: '',
    LicenseAddress: '',
    OwnerName: '',
    PropAddress: '',
    SquareFeet: '',
    Purpose: ''
  });
  const [taxRate, setTaxRate] = useState<number>(0);

  // Dependency function to handle the change in the field value
  function handleDependency(val: string, { name }) {
    const data = { ...applicationDetails };
    data[name] = val;
    setApplicationDetails(data);
  }

  // All the fields required for the form using formik
  const allFields: Fields[] = [
    {
      fieldName: 'ApplicantName',
      fieldLabel: 'Applicant Name',
      fieldType: 'text',
      required: true,
      initialValue: applicationDetails.ApplicantName,
      dependency: handleDependency
    },
    {
      fieldName: 'BlockLot',
      fieldLabel: 'Block Lot',
      options: blockLotsOptions,
      fieldType: 'autocomplete',
      required: true,
      initialValue: applicationDetails.BlockLot || '',
      dependency: async (val: any) => {
        if (val?.length > 2) {
          const blockLotsData = await getBlockLots(val);
          setBlockLotsOptions(blockLotsData);
        } else if (typeof val === 'object') {
          const data = { ...applicationDetails };
          data.OwnerName = val.OwnerName;
          data.BlockLot = val.BlockLot;
          data.LicenseAddress = val.LicenseAddress;
          data.PropAddress = val.PropAddress;
          setApplicationDetails(data);
        }
      }
    },
    {
      fieldName: 'ApplicantAddress',
      fieldLabel: 'Applicant Address',
      fieldType: 'note',
      required: true,
      initialValue: applicationDetails.ApplicantAddress,
      dependency: handleDependency
    },
    {
      fieldName: 'LicensedAddress',
      fieldLabel: 'License Address',
      fieldType: 'note',
      required: true,
      initialValue: applicationDetails.LicenseAddress || ''
    },
    {
      fieldName: 'SquareFeet',
      fieldLabel: 'Square ft ',
      fieldType: 'number',
      required: true,
      initialValue: applicationDetails.SquareFeet,
      dependency: handleDependency
    },
    {
      fieldName: 'OwnerName',
      fieldLabel: 'Owner Name',
      fieldType: 'text',
      required: true,
      initialValue: applicationDetails.OwnerName || ''
    },
    {
      fieldName: 'Purpose',
      fieldLabel: 'Purpose',
      fieldType: 'note',
      initialValue: applicationDetails.Purpose,
      dependency: handleDependency
    },
    {
      fieldName: 'OwnerAddress',
      fieldLabel: 'Owner Address ',
      fieldType: 'note',
      required: true,
      initialValue: applicationDetails.PropAddress || ''
    },
    {
      fieldName: 'DocumentFileUpload',
      fieldLabel: 'Upload Document',
      fieldType: 'file',
      isErrorField: false,
      required: true,
      listUI: true,
      placement: 'start',
      multiSelect: false,
      disabled: false,
      aboveParagraph: <Typography>
        A sketch of the lot showing dimensions and locations must be uploaded. If the entire lot is not included in this application,
        indicate on sketch the part to be licensed with it's dimensions.
        <br /> Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF files are allowed, with maximum file size of 10MB.
      </Typography>
    }
  ];

  // Function to handle the payment and proceed to payment
  const handleProceedToPay = async (data: any) => {
    data.ApplicationFee = feeDetails?.applicationFee;
    const response = await httpRequest('POST', `/ucd_application/insert`, data);
    if (response?.status === 'success') {
      toast.success('Application submitted successfully.');
      const paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries(feeDetails).filter(([, value]) => value > 0)
        ),
        ...(taxRate > 0 ? { taxRate } : {})
      };

      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: response?.data?.ApplicationID,
          applicationStatus: 'Draft',
          statusPayload: 'In Review',
          permitPrefix: 'UsedCar'
        }
      };
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // Function to insert the application
  const handleInsertApplication = async (data: any, type = '') => {
    data.ApplicationFee = feeDetails?.applicationFee;
    setLoading(true);

    const response = await httpRequest('POST', `/ucd_application/insert`, data);
    if (response?.status === 'success') {
      if (type) {
        return true;
      }
      toast.success(`Application submitted successfully.`);
      if (clickedOn === 'Save Draft') {
        setLoading(false);
        navigate(`/${townshipId}/Clerk Department/${permit}/draft-application`);
      } else {
        navigate(`/${townshipId}/Clerk Department/${permit}/application-list`);
      }
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // Function to handle the form submission
  const handleSubmit = async (data) => {
    const document = await fileUpload(data.DocumentFileUpload, 'UsedCar');
    data.DocumentFileUpload = document;
    if (clickedOn === 'Proceed to Payment') {
      handleProceedToPay(data);
    } else if (clickedOn === 'Save Draft') {
      handleInsertApplication(data);
    }
  };

  // Function to get the application fee
  const handleGetApplicationFee = async () => {
    setLoading(true);
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${permit}`
    );
    if (response?.status === 'success') {
      const feeObject = getFeesForCurrentPermit(response.data);
      setTaxRate(feeObject?.CreditCardFee || 0);
      setFeeDetails({
        applicationFee: Number(feeObject?.ApplicationFee)
      });
      setLoading(false);
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // UseEffect to get the application fee
  useEffect(() => {
    if (permit) {
      handleGetApplicationFee();
    }
  }, []);

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb
        PermitName={'Clerk Used Car Dealer'}
        PageTitle={'New Application'}
      />

      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType={'new'}
      />

      <Card>
        <Box
          m={{ md: 3, xs: 1 }}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h4">New Application</Typography>
          <PdfGenerator
            PermitName={'Clerk used car dealer'}
            allFields={allFields}
          />
        </Box>

        <Box m={{ md: 3, xs: 1 }}>
          <FormikForm
            allFields={allFields}
            buttonPlacement={'right'}
            buttonTitle={['Cancel', 'Save Draft', 'Proceed to Payment']}
            setClickedOn={setClickedOn}
            clickProp
            content={
              <>
                <Box display={'flex'} gap={2}>
                  <TextField
                    id="outlined-basic"
                    disabled
                    sx={{ mt: 2 }}
                    value={`$ ${feeDetails?.applicationFee}.00` || 0}
                    label="Application Fee"
                    variant="outlined"
                    InputLabelProps={{
                      className: 'weight-600'
                    }}
                  />
                </Box>
              </>
            }
            formMethod={handleSubmit}
            heading={'Clerk Department'}
            handleOtherButton={() => {
              navigate(`/dashboard/${permit}`);
            }}
          />
        </Box>
      </Card>
    </Container>
  );
};

export default NewApplication;
